import { deepTransformDate, deepTransformTimestamp } from '../../utils/date';
import { Uuid } from '../Base';
import { RepositoryElement } from '../Repository';
import { CashBalance, CashBalanceJson } from '../UserStore/Account';

export enum ReferralSystem {
  Joyajoy = 'joyajoy',
}

export enum RewardReferralRole {
  Partner = 'partner',
  Distributor = 'distributor',
  Customer = 'customer',
}

export interface RewardReferralJson<T = number> {
  userId: Uuid;
  referralSystem: ReferralSystem;
  role: RewardReferralRole;
  referredBy: Uuid | null;
  referredAt: T;
  expiredAt?: T; // Partner Distributor
  referralReward?: CashBalanceJson; // Partner
  managementReward?: CashBalanceJson; // Distributor
  lastPurchasedAt?: T; // Customer
  partnerId?: Uuid | null; // Customer
  distributorId?: Uuid | null; // Customer
  partnerReward?: CashBalanceJson; // Customer
  distributorReward?: CashBalanceJson; // Customer
  tier1CustomerReward?: CashBalanceJson; // Customer
  tier2CustomerReward?: CashBalanceJson; // Customer

  rewardReferralCode?: RewardReferralCode;
}

export class RewardReferral
  extends RepositoryElement
  implements RewardReferralJson<Date>
{
  userId!: Uuid;
  referralSystem!: ReferralSystem;
  role!: RewardReferralRole;
  referredBy!: Uuid | null;
  referredAt!: Date;
  expiredAt?: Date | undefined;
  referralReward?: CashBalance | undefined;
  managementReward?: CashBalance | undefined;
  lastPurchasedAt?: Date | undefined;
  partnerId?: string | null | undefined;
  distributorId?: string | null | undefined;
  partnerReward?: CashBalance | undefined;
  distributorReward?: CashBalance | undefined;
  tier1CustomerReward?: CashBalance | undefined;
  tier2CustomerReward?: CashBalance | undefined;

  rewardReferralCode?: RewardReferralCode;

  constructor(rewardReferral: RewardReferralJson<Date | number>) {
    super();
    const {
      referralReward,
      managementReward,
      partnerReward,
      distributorReward,
      tier1CustomerReward,
      tier2CustomerReward,
      ...rest
    } = deepTransformTimestamp(rewardReferral) as RewardReferralJson<Date>;
    this.referralReward = referralReward
      ? new CashBalance(referralReward)
      : undefined;
    this.managementReward = managementReward
      ? new CashBalance(managementReward)
      : undefined;
    this.partnerReward = partnerReward
      ? new CashBalance(partnerReward)
      : undefined;
    this.distributorReward = distributorReward
      ? new CashBalance(distributorReward)
      : undefined;
    this.tier1CustomerReward = tier1CustomerReward
      ? new CashBalance(tier1CustomerReward)
      : undefined;
    this.tier2CustomerReward = tier2CustomerReward
      ? new CashBalance(tier2CustomerReward)
      : undefined;
    Object.assign(this, rest);
  }

  id() {
    return `${this.referralSystem}_${this.userId}`;
  }

  toJSON(): RewardReferralJson {
    return deepTransformDate(this) as unknown as RewardReferralJson;
  }

  get isPartnerRole() {
    return this.role === RewardReferralRole.Partner;
  }

  get isDistributorRole() {
    return this.role === RewardReferralRole.Distributor;
  }

  get isPartnerOrDistributorRole() {
    return this.isPartnerRole || this.isDistributorRole;
  }

  get referralShareId() {
    if (this.role === RewardReferralRole.Partner) {
      return this.partnerId;
    }
    if (this.role === RewardReferralRole.Distributor) {
      return this.distributorId;
    }
    return undefined;
  }

  get isExpired(): boolean {
    const now = new Date();
    return this.expiredAt ? now > this.expiredAt : false;
  }

  getConfirmReward(role: RewardReferralRole, isTier2?: boolean) {
    if (role === RewardReferralRole.Distributor) {
      if (this.role === RewardReferralRole.Partner) {
        return this.referralReward?.confirmed;
      }
      if (this.role === RewardReferralRole.Distributor) {
        return this.referralReward?.confirmed;
      }
      if (this.role === RewardReferralRole.Customer) {
        return this.distributorReward?.confirmed;
      }
    }
    if (role === RewardReferralRole.Partner) {
      if (this.role === RewardReferralRole.Partner) {
        return this.referralReward?.confirmed;
      }
      if (this.role === RewardReferralRole.Distributor) {
        return this.referralReward?.confirmed;
      }
      if (this.role === RewardReferralRole.Customer) {
        return this.partnerReward?.confirmed;
      }
    }
    if (role === RewardReferralRole.Customer) {
      if (this.role === RewardReferralRole.Customer) {
        if (isTier2) {
          return this.tier2CustomerReward?.confirmed;
        }
        return this.tier1CustomerReward?.confirmed;
      }
    }
    return undefined;
  }
}

export interface RewardReferralCode {
  referralCode: string; // 12 digit
  userId: Uuid;
  referralSystem: ReferralSystem;
}
